import type { ReactNode } from 'react'

/**
 * Renders a help article's body (#120).
 *
 * A deliberately small markdown subset — `##` headings, paragraphs, `-`
 * bullets, `1.` steps, `**bold**`, `` `code` `` and links — parsed here rather
 * than by a library. Two reasons, in order of weight:
 *
 * 1. There is no markdown pipeline in this repo, and adding one (plus its
 *    sanitiser, plus a plugin chain) to render help pages is a large dependency
 *    for a small job.
 * 2. A subset the renderer fully covers cannot half-parse. A general parser
 *    meeting input it does not expect degrades into visible syntax on the page;
 *    this one either understands a line or renders it as a paragraph, which is
 *    always readable.
 *
 * Nothing here interprets HTML, so article bodies cannot inject markup — the
 * corpus is authored in-repo and reviewed, but the renderer does not rely on
 * that being true.
 */

type Block =
  | { items: string[]; kind: 'bullets' }
  | { items: string[]; kind: 'steps' }
  | { kind: 'heading'; text: string }
  | { kind: 'paragraph'; text: string }

const BULLET = /^-\s+/
const STEP = /^\d+\.\s+/
/** Blocks are separated by one or more blank lines. */
const BLOCK_BREAK = /\n{2,}/
/** A whole token that is exactly a link, captured as text and href. */
const LINK = /^\[([^\]]+)\]\(([^)]+)\)$/

/** Blank-line separated chunks, each classified by how its lines start. */
const parseBlocks = (body: string): Block[] =>
  body
    .trim()
    .split(BLOCK_BREAK)
    .map((chunk) => {
      const lines = chunk.split('\n').filter((line) => line.trim() !== '')
      if (lines.length === 0) {
        return null
      }
      const [first] = lines
      if (first?.startsWith('## ') === true) {
        return { kind: 'heading', text: first.slice(3).trim() } satisfies Block
      }
      if (lines.every((line) => BULLET.test(line))) {
        return {
          items: lines.map((line) => line.replace(BULLET, '')),
          kind: 'bullets',
        } satisfies Block
      }
      if (lines.every((line) => STEP.test(line))) {
        return {
          items: lines.map((line) => line.replace(STEP, '')),
          kind: 'steps',
        } satisfies Block
      }
      // A wrapped paragraph rejoins with spaces: the source is authored at a
      // readable width, and the browser does the wrapping.
      return { kind: 'paragraph', text: lines.join(' ') } satisfies Block
    })
    .filter((block): block is Block => block !== null)

/** `**bold**`, `` `code` ``, `[text](href)` — everything else is plain text. */
const INLINE = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g

/**
 * Bold recurses into its own content; the other two are leaves.
 *
 * Without it, `**[Arquivos](/ajuda/arquivos)**` renders the link SYNTAX as
 * literal text inside a `<strong>` — the bold alternative wins the match
 * because its `[^*]+` happily swallows the whole link. That is the exact
 * half-parse this renderer exists to avoid, and it is invisible in review: the
 * page still lays out, it just has brackets in it. A list of bolded links to
 * other articles is the natural way to write a hub section, so it recurs.
 *
 * The recursion terminates because a bold token cannot contain `*`, so it can
 * never hold another bold token — only a link or a code span.
 */
const renderInline = (text: string): ReactNode[] =>
  text
    .split(INLINE)
    .filter((token) => token !== '')
    .map((token, position) => {
      const key = `${position}:${token}`
      if (token.startsWith('**') && token.endsWith('**')) {
        return (
          <strong className="font-semibold text-[#0b0b0d]" key={key}>
            {renderInline(token.slice(2, -2))}
          </strong>
        )
      }
      if (token.startsWith('`') && token.endsWith('`')) {
        return (
          <code className="rounded bg-black/5 px-1 py-0.5 font-mono text-[0.9em]" key={key}>
            {token.slice(1, -1)}
          </code>
        )
      }
      const link = LINK.exec(token)
      if (link !== null) {
        return (
          <a className="underline underline-offset-2 hover:opacity-70" href={link[2]} key={key}>
            {link[1]}
          </a>
        )
      }
      return token
    })

export const ArticleBody = ({ body }: { body: string }) => (
  <div className="space-y-5 text-[#37373d] text-[1.0625rem] leading-[1.75]">
    {parseBlocks(body).map((block, position) => {
      const key = `${position}:${block.kind}`
      if (block.kind === 'heading') {
        return (
          <h2
            className="pt-4 font-semibold text-[#0b0b0d] text-[1.375rem] leading-snug tracking-[-0.01em]"
            key={key}
          >
            {block.text}
          </h2>
        )
      }
      if (block.kind === 'bullets') {
        return (
          <ul className="space-y-2 pl-5" key={key}>
            {block.items.map((item) => (
              <li className="list-disc marker:text-black/30" key={item}>
                {renderInline(item)}
              </li>
            ))}
          </ul>
        )
      }
      if (block.kind === 'steps') {
        return (
          <ol className="space-y-2 pl-5" key={key}>
            {block.items.map((item) => (
              <li className="list-decimal marker:text-black/40 marker:tabular-nums" key={item}>
                {renderInline(item)}
              </li>
            ))}
          </ol>
        )
      }
      return <p key={key}>{renderInline(block.text)}</p>
    })}
  </div>
)
